// src/api/auth/mfaApi.ts
// identity's MFA continuation, enrolment, step-up and passkey endpoints (contract §6.1, §6.3, §6.6–§6.8).

import { http } from "../../lib/http/client";
import { authBase } from "./authApi";
import type { LoginAuthenticated } from "../../lib/http/session";
import type {
	AssertionCredentialJSON,
	AttestationCredentialJSON,
	CodeSent,
	EnrolmentCodeSent,
	MfaFactorsResponse,
	PasskeyOptions,
	PasskeyStepUpOptions,
	PasskeySummary,
	PasswordLoginResponse,
	PublicKeyCreationOptionsJSON,
	PublicKeyRequestOptionsJSON,
	RecoveryCodesResponse,
	RecoveryMethod,
	SmsConfirmResponse,
	SmsEnrolment,
	StepUpResponse,
	TotpConfirmResponse,
	TotpEnrolment,
} from "../../types/auth/mfa";

export const verifyMfaChallenge = async (challengeId: string, code: string): Promise<LoginAuthenticated> => {
	const response = await http.post<LoginAuthenticated>(`${authBase()}/mfa/verify`, { challenge_id: challengeId, method: "totp", code });
	return response.data;
};

export const startMfaRecovery = async (challengeId: string, method: "sms" | "email"): Promise<CodeSent> => {
	const response = await http.post<CodeSent>(`${authBase()}/mfa/recover/start`, { challenge_id: challengeId, method });
	return response.data;
};

export const verifyMfaRecovery = async (challengeId: string, method: RecoveryMethod, code: string): Promise<LoginAuthenticated> => {
	const response = await http.post<LoginAuthenticated>(`${authBase()}/mfa/recover/verify`, { challenge_id: challengeId, method, code });
	return response.data;
};

// challengeId null starts a passwordless sign-in with a discoverable credential.
export const fetchPasskeyLoginOptions = async (challengeId: string | null): Promise<PasskeyOptions<PublicKeyRequestOptionsJSON>> => {
	const response = await http.post<PasskeyOptions<PublicKeyRequestOptionsJSON>>(`${authBase()}/passkeys/login/options`, { challenge_id: challengeId });
	return response.data;
};

export const verifyPasskeyLogin = async (flowId: string, credential: AssertionCredentialJSON): Promise<LoginAuthenticated> => {
	const response = await http.post<LoginAuthenticated>(`${authBase()}/passkeys/login/verify`, { flow_id: flowId, credential });
	return response.data;
};

export const fetchMfaFactors = async (): Promise<MfaFactorsResponse> => {
	const response = await http.get<MfaFactorsResponse>(`${authBase()}/mfa/factors`);
	return response.data;
};

export const requestEnrolmentEmailCode = async (): Promise<EnrolmentCodeSent> => {
	const response = await http.post<EnrolmentCodeSent>(`${authBase()}/mfa/enrolment-code`);
	return response.data;
};

export const enrolTotp = async (): Promise<TotpEnrolment> => {
	const response = await http.post<TotpEnrolment>(`${authBase()}/mfa/totp/enrol`);
	return response.data;
};

export const confirmTotp = async (factorId: string, code: string, emailCode?: string): Promise<TotpConfirmResponse> => {
	const response = await http.post<TotpConfirmResponse>(`${authBase()}/mfa/totp/confirm`, {
		factor_id: factorId,
		code,
		...(emailCode ? { email_code: emailCode } : {}),
	});
	return response.data;
};

export const enrolRecoveryPhone = async (phone: string, emailCode?: string): Promise<SmsEnrolment> => {
	const response = await http.post<SmsEnrolment>(`${authBase()}/mfa/sms/enrol`, { phone, ...(emailCode ? { email_code: emailCode } : {}) });
	return response.data;
};

export const confirmRecoveryPhone = async (factorId: string, code: string, emailCode?: string): Promise<SmsConfirmResponse> => {
	const response = await http.post<SmsConfirmResponse>(`${authBase()}/mfa/sms/confirm`, {
		factor_id: factorId,
		code,
		...(emailCode ? { email_code: emailCode } : {}),
	});
	return response.data;
};

export const deleteMfaFactor = async (factorId: string): Promise<void> => {
	await http.delete(`${authBase()}/mfa/factors/${encodeURIComponent(factorId)}`);
};

export const regenerateRecoveryCodes = async (): Promise<RecoveryCodesResponse> => {
	const response = await http.post<RecoveryCodesResponse>(`${authBase()}/mfa/recovery-codes`);
	return response.data;
};

export const stepUpWithTotp = async (code: string): Promise<StepUpResponse> => {
	const response = await http.post<StepUpResponse>(`${authBase()}/stepup`, { method: "totp", code });
	return response.data;
};

export const fetchPasskeyStepUpOptions = async (): Promise<PasskeyStepUpOptions> => {
	const response = await http.post<PasskeyStepUpOptions>(`${authBase()}/passkeys/stepup/options`);
	return response.data;
};

export const stepUpWithPasskey = async (flowId: string, credential: AssertionCredentialJSON): Promise<StepUpResponse> => {
	const response = await http.post<StepUpResponse>(`${authBase()}/stepup`, { method: "passkey", flow_id: flowId, credential });
	return response.data;
};

export const fetchPasskeyRegistrationOptions = async (): Promise<PasskeyOptions<PublicKeyCreationOptionsJSON>> => {
	const response = await http.post<PasskeyOptions<PublicKeyCreationOptionsJSON>>(`${authBase()}/passkeys/register/options`);
	return response.data;
};

export const verifyPasskeyRegistration = async (
	flowId: string,
	name: string,
	credential: AttestationCredentialJSON,
	emailCode?: string
): Promise<{ passkey: PasskeySummary }> => {
	const response = await http.post<{ passkey: PasskeySummary }>(`${authBase()}/passkeys/register/verify`, {
		flow_id: flowId,
		name,
		credential,
		...(emailCode ? { email_code: emailCode } : {}),
	});
	return response.data;
};

export const fetchPasskeys = async (): Promise<{ passkeys: PasskeySummary[] }> => {
	const response = await http.get<{ passkeys: PasskeySummary[] }>(`${authBase()}/passkeys`);
	return response.data;
};

export const deletePasskey = async (passkeyId: string): Promise<void> => {
	await http.delete(`${authBase()}/passkeys/${encodeURIComponent(passkeyId)}`);
};

export const setPasswordLogin = async (enabled: boolean): Promise<PasswordLoginResponse> => {
	const response = await http.put<PasswordLoginResponse>(`${authBase()}/password-login`, { enabled });
	return response.data;
};
